import React, { useContext, useEffect, useState } from "react";
import { FormControl, InputLabel, MenuItem, Select } from "@material-ui/core";
import {
  boostTiers as BoostTierNames,
  STRIPE_SUBSCRIPTION_STATUSES,
} from "components/Pages/Boost/consts";
import { ModalContext, Pill } from "components/common";
import {
  canDowngradeTerm,
  canDowngradeTier,
  BoostTermNames,
  tierColors,
  termColors,
} from "./helpers";

const DISABLED_REASONS = {
  BASIC_SUBSCRIPTION: "cannot downgrade below the lowest tier/term",
  EXISTING_SCHEDULE:
    "downgrade scheduled - release existing schedule to change",
  NO_CHANGE: "pick a lower tier/term to downgrade",
  SUBSCRIPTION_NOT_ACTIVE:
    "cannot scheduled downgrade for inactive subscription",
};

const DowngradeButton = ({ disabled, handleDowngrade }) => (
  <button
    type="button"
    disabled={disabled}
    className={`${
      disabled ? "bg-gray" : "bg-red-500 hover:bg-red-600"
    } self-end text-white px-3 py-1 mt-4`}
    onClick={handleDowngrade}
  >
    Downgrade Subscription
  </button>
);

const DowngradeControls = ({
  isPickerDisabled,
  productTerm,
  productTier,
  selectedTerm,
  selectedTier,
  setSelectedTerm,
  setSelectedTier,
}) => {
  return (
    <>
      <p className="my-2">Downgrade to:</p>
      <div className="flex">
        <FormControl style={{ minWidth: 120 }} disabled={isPickerDisabled}>
          <InputLabel id="boost-subscription-tier-selection">Tier</InputLabel>
          <Select
            labelId="boost-subscription-tier-selection"
            value={selectedTier}
            label="Select Tier"
            onChange={(e) => setSelectedTier(e.target.value)}
          >
            <MenuItem
              disabled={!canDowngradeTier(productTier, BoostTierNames.BASIC)}
              value={BoostTierNames.BASIC}
            >
              Basic
            </MenuItem>
            <MenuItem
              disabled={!canDowngradeTier(productTier, BoostTierNames.PLUS)}
              value={BoostTierNames.PLUS}
            >
              Plus
            </MenuItem>
            <MenuItem
              disabled={!canDowngradeTier(productTier, BoostTierNames.PREMIUM)}
              value={BoostTierNames.PREMIUM}
            >
              Premium
            </MenuItem>
            <MenuItem
              disabled={!canDowngradeTier(productTier, BoostTierNames.VIP)}
              value={BoostTierNames.VIP}
            >
              VIP
            </MenuItem>
          </Select>
        </FormControl>
        <div className="ml-4">
          <FormControl style={{ minWidth: 120 }} disabled={isPickerDisabled}>
            <InputLabel id="boost-subscription-term-selection">Term</InputLabel>
            <Select
              labelId="boost-subscription-term-selection"
              value={selectedTerm}
              label="Select Term"
              onChange={(e) => setSelectedTerm(e.target.value)}
            >
              <MenuItem
                disabled={
                  !canDowngradeTerm(productTerm, BoostTermNames.monthly)
                }
                value={BoostTermNames.monthly}
              >
                Monthly
              </MenuItem>
              <MenuItem
                disabled={!canDowngradeTerm(productTerm, BoostTermNames.yearly)}
                value={BoostTermNames.yearly}
              >
                Yearly
              </MenuItem>
            </Select>
          </FormControl>
        </div>
      </div>
    </>
  );
};

const ScheduleDetails = ({ scheduledUpdate }) => {
  const { term, tier } = scheduledUpdate;
  return (
    <div className="pr-2">
      <div className="flex justify-between">
        <span>Downgrading to:</span>
        <div className="inline-flex">
          <Pill
            label={tier}
            pillColor={tierColors[tier]}
            className="mr-0 rounded-l-full"
          />
          <Pill
            label={term}
            pillColor={termColors[term]}
            className="rounded-r-full"
          />
        </div>
      </div>
      <div className="flex justify-between">
        <span>Scheduled for:</span>
        <span className="font-bold">
          {new Date(scheduledUpdate.startDate * 1000).toDateString()}
        </span>
      </div>
    </div>
  );
};

/**
 * @param {Object} props
 * @param {string} props.email - (Required) The email associated with the subscription.
 * @param {Date} props.endOfBillingCycle - (Required) End of current billing cycle
 * @param {string} props.productTerm - (Required) The current subscription term.
 * @param {string} props.productTier - (Required) The current subscription tier.
 * @param {Object} props.scheduledUpdate - Indicates whether current subscription has something scheduled
 * @param {number} props.scheduledUpdate.startDate - Start date of current schedule
 * @param {boolean} props.subscriptionStatus Stripe subscription status
 */
const BoostSubscriptionDowngrade = (props) => {
  const {
    email,
    endOfBillingCycle,
    productTerm,
    productTier,
    scheduledCancellationDate,
    scheduledUpdate,
    subscriptionStatus,
  } = props;

  const isCanceled = !!scheduledCancellationDate;
  const isDowngradeScheduled = !!scheduledUpdate?.startDate;
  const isSubscriptonStatusOngoing = [
    STRIPE_SUBSCRIPTION_STATUSES.ACTIVE,
    STRIPE_SUBSCRIPTION_STATUSES.PAST_DUE,
  ].includes(subscriptionStatus);
  const isPickerDisabled =
    !isSubscriptonStatusOngoing || scheduledUpdate?.startDate;
  const isPastDue =
    subscriptionStatus === STRIPE_SUBSCRIPTION_STATUSES.PAST_DUE;

  const { openModal } = useContext(ModalContext);
  const [selectedTier, setSelectedTier] = useState(productTier);
  const [selectedTerm, setSelectedTerm] = useState(productTerm);

  const isSelectedDifferent =
    selectedTier !== productTier || selectedTerm !== productTerm;
  // downgrade button gets disabled when there is no change in picker, in addition to disabling picker
  const isDowngradeDisabled = isPickerDisabled || !isSelectedDifferent;

  useEffect(() => {
    // after creating a new schedule selected tier & term stays same
    // this use effect gets triggered once on render and each time existingSchedule changes
    // ideally existingSchedule should change only once as we do not support releasing schedule yet
    setSelectedTerm(productTerm);
    setSelectedTier(productTier);
  }, [scheduledUpdate]);

  const isMostBasicSubscription =
    productTerm === BoostTermNames.monthly &&
    productTier === BoostTierNames.BASIC;
  let disableReason = "";
  if (isDowngradeDisabled) {
    if (isMostBasicSubscription) {
      disableReason = DISABLED_REASONS.BASIC_SUBSCRIPTION;
    } else if (scheduledUpdate?.startDate) {
      disableReason = DISABLED_REASONS.EXISTING_SCHEDULE;
    } else if (!isSelectedDifferent) {
      disableReason = DISABLED_REASONS.NO_CHANGE;
    } else if (!isSubscriptonStatusOngoing) {
      disableReason = DISABLED_REASONS.SUBSCRIPTION_NOT_ACTIVE;
    }
  }

  const handleDowngrade = (e) => {
    openModal(e, "DowngradeBoostSubscriptionModal", {
      email,
      subscriptionStatus,
      endOfBillingCycle,
      selectedTerm,
      selectedTier,
    });
  };

  const tips = [];
  if (isCanceled) {
    tips.push("the subscription cannot be downgraded if it is set to cancel");
  }
  if (disableReason) {
    tips.push(disableReason);
  }

  return (
    <div className="flex flex-col w-full">
      {isDowngradeScheduled ? (
        <ScheduleDetails scheduledUpdate={scheduledUpdate} />
      ) : (
        <span>
          No downgrade scheduled. Downgrade to schedule a change at the end of
          the billing cycle. Past-due subscriptions downgrade immediately.
        </span>
      )}
      {isDowngradeScheduled ? (
        <span className="mt-2">
          You must release the subscription schedule to modify the downgrade.
        </span>
      ) : (
        <DowngradeControls
          isPickerDisabled={isPickerDisabled}
          productTerm={productTerm}
          productTier={productTier}
          selectedTerm={selectedTerm}
          selectedTier={selectedTier}
          setSelectedTerm={setSelectedTerm}
          setSelectedTier={setSelectedTier}
        />
      )}
      {!!tips.length &&
        tips.map((tip) => (
          <p className="mt-2 text-sm italic text-gray-700">{tip}</p>
        ))}
      <DowngradeButton
        disabled={isDowngradeDisabled || isCanceled}
        handleDowngrade={handleDowngrade}
      />
      <p className="text-sm self-end text-gray-700">
        {isPastDue ? "downgrade immediately" : "schedule downgrade"}
      </p>
    </div>
  );
};

export default BoostSubscriptionDowngrade;
